// ─── My Calendar — vue mois + événements ──────────────────────────
// Grille mensuelle avec pastilles, panneau du jour sélectionné, ajout/édition/
// suppression d'événements. Données PARTAGÉES avec MoPCal (mêmes docs) via le
// calendrier hérité de l'espace (useEspace().tenantRangement).
import React, { useEffect, useMemo, useState, useRef } from 'react';
import { View, Text, TextInput, TouchableOpacity, ScrollView, StyleSheet, Dimensions, Keyboard, Switch, ActivityIndicator } from 'react-native';
import { Gesture, GestureDetector } from 'react-native-gesture-handler';
import {
  useUI, useAuth, useEspace, PageHeader, EmptyState, Feuille, LigneSwipe, SectionPliable,
  styleChampErreur, LigneErreur, ContenuAVenir,
  confirmerSuppression, confirmerChoix, useMenuOngletActif, useDoubleTapOngletActif, useResetOnglet, usePeutUtiliser, AideFonction,
  SelecteurDate, SelecteurHeure, PiecesJointes, useModifie, HIT, useT, frJM, ChoixPuces, lienItineraire, demanderItineraire,
} from '@mop/core';
import {
  dateKey, ecouterEvenements, ajouterEvenement, modifierEvenement, supprimerEvenement,
  ecouterEvenementsRecurrents, ajouterEvenementRecurrent, supprimerEvenementRecurrent, modifierEvenementRecurrent, retirerExceptionRecurrent,
  deplacerOccurrenceRecurrente, majDeplacementRecurrent, nettoyerDeplacementRecurrent, lireEvenementRecurrent,
  ajouterExceptionRecurrent, fusionner, JOURS_SEM,
} from './services/evenements';
import { CATEGORIES_EVT, TYPE_FILTRES, emojiCategorie, labelCategorie, labelTypeFiltre, estMasqueEvenement, typeEvenement, labelTypeEvenement } from './data/categories';
import { CONFIG_DEFAUT, chargerAffichageConfig, sauvegarderAffichageConfig, parseHeureMin } from './services/affichageConfig';
import FeuilleVacances from './FeuilleVacances';
import EcranEDT from './EcranEDT';
import { ecouterGrillesEDT, genererOccurrencesEDT } from './services/edt';
import FeuilleGardes from './FeuilleGardes';
import FeuilleConfigCalendrier from './FeuilleConfigCalendrier';
import EcranDemandes from './EcranDemandes';
import GrilleHoraire from './GrilleHoraire';
import EcranLieux from './EcranLieux';
import FeuilleChoixLieu from './FeuilleChoixLieu';
import VueAnnee from './VueAnnee';
import FicheEvenement from './FicheEvenement';
import FormulaireEvenement from './FormulaireEvenement';
import GrilleMois from './GrilleMois';
import FeuilleEvenementsJour from './FeuilleEvenementsJour';
import FeuilleRecurrences from './FeuilleRecurrences';
import FeuilleFiltres from './FeuilleFiltres';
import useEvenementsSuperposes from './hooks/useEvenementsSuperposes';
import useGardeAffichage from './hooks/useGardeAffichage';
import useFormulaireEvenement from './hooks/useFormulaireEvenement';
import { FontAwesome, FontAwesome5 } from '@expo/vector-icons';
import { useCalendrierActif } from './CalendrierActifContext';
import PremierCalendrier from './PremierCalendrier';
import SwitcherCalendriers from './SwitcherCalendriers';
import SelecteurMoisAnnee from './SelecteurMoisAnnee';

// Couleurs fidèles à MoPCal.
const COL_VAC = '#B2DFDB';   // barre de vacances (teal)
const COL_FERIE = '#EF5350'; // barre de jour férié (rouge)
const COL_EXC = '#E8A33D';   // anneau = échange / exception de garde (ambre)

// REPETS/JOURS_SEM : déplacés dans FormulaireEvenement / services/evenements
// (découpage) — le formulaire et la fiche sont des composants dédiés.
const JOURS = ['L', 'M', 'M', 'J', 'V', 'S', 'D'];
const MOIS = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
  'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];

const premierJourCol = (a, m) => (new Date(a, m, 1).getDay() + 6) % 7;   // 0 = lundi
const nbJours = (a, m) => new Date(a, m + 1, 0).getDate();
const fmtJour = (dk) => { if (!/^\d{4}-\d{2}-\d{2}$/.test(dk || '')) return '—'; const [y, m, d] = dk.split('-'); return `${d}/${m}/${y}`; };
// Numéro de semaine ISO 8601 (lundi = 1er jour, la semaine du 1er jeudi = S1).
const numSemaine = (d) => {
  const date = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  date.setUTCDate(date.getUTCDate() - ((date.getUTCDay() + 6) % 7) + 3);
  const jeudi1 = new Date(Date.UTC(date.getUTCFullYear(), 0, 4));
  return 1 + Math.round(((date - jeudi1) / 86400000 - 3 + ((jeudi1.getUTCDay() + 6) % 7)) / 7);
};
// Hauteur de la grille horaire (vues Semaine / Jour) : ~58 % de l'écran.

export default function EcranCalendrier({ mode = 'edit', ongletCle = 'Calendrier' }) {
  const UI = useUI();
  const t = useT();
  const MOIS = t('commun.moisLongs');           // noms de mois localisés
  const joursL = t('commun.joursCourts');        // initiales de jours (L→D / M→S)
  // Libellé traduit d'un type de répétition (REPETS = données module sans accès à t).
  const repetLabel = (id) => {
    const m = { '': 'commun.non', quotidien: 'calendrier.repetQuotidien', hebdo: 'calendrier.repetHebdo', bimensuel: 'calendrier.repetBimensuel', mensuel: 'calendrier.repetMensuel' };
    return m[id] ? t(m[id]) : (id || '');
  };
  // Libellés COURTS des puces de répétition (tiennent sur UNE ligne) — le
  // stepper « Tous les n … » et la fiche gardent les libellés longs.
  const repetCourt = (id) => {
    const m = { '': 'commun.non', quotidien: 'calendrier.repetJourCourt', hebdo: 'calendrier.repetSemCourt', mensuel: 'calendrier.repetMoisCourt' };
    return m[id] ? t(m[id]) : (id || '');
  };
  const s = useMemo(() => makeStyles(UI), [UI]);
  const { user } = useAuth();
  const { espaceActif, tenantRangement } = useEspace();
  const { calIdActif, calendriers, visiblesIds, estVisible, toggleVisible, setCalIdActif, couleurDe, pret, aCalendrierEspace, peutCreer, erreurLectureCal } = useCalendrierActif();
  const edit = mode === 'edit';
  const calId = calIdActif || tenantRangement;
  // Itinéraire d'un événement : résout SON lieu (par lieuId) dans le calendrier au
  // moment de l'affichage → l'adresse suit toujours la page Lieux (pas de copie figée
  // sur l'événement). Repli sur le nom mémorisé si le lieu a été supprimé.
  const urlItineraireEv = (ev) => {
    if (!ev || (!ev.lieu && !ev.lieuId)) return '';
    const cal = calendriers.find(c => c.id === (ev._calId || calId));
    const lo = ev.lieuId ? (cal?.lieux || []).find(l => l.id === ev.lieuId) : null;
    return lienItineraire({ lat: lo?.lat, lng: lo?.lng, adresse: lo?.adresse || ev.lieu });
  };
  // L'URL sert a SAVOIR s'il y a un itineraire (affichage de la boussole) ;
  // l'ouverture, elle, demande d'abord l'application de navigation.
  const ouvrirItineraireEv = (ev) => {
    if (!ev || (!ev.lieu && !ev.lieuId)) return;
    const cal = calendriers.find(c => c.id === (ev._calId || calId));
    const lo = ev.lieuId ? (cal?.lieux || []).find(l => l.id === ev.lieuId) : null;
    demanderItineraire({ lat: lo?.lat, lng: lo?.lng, adresse: lo?.adresse || ev.lieu },
      { titre: t('lieux.itineraire'), annuler: t('commun.annuler') });
  };

  // ── Portes de forfait ──────────────────────────────────────────
  // Une fonction non autorisée ne doit RIEN laisser voir : pas d'événement,
  // pas de garde, pas de vue horaire. Les portes sont lues ICI, en haut, parce
  // que la suite de l'écran (garde, menus, rendu) en dépend.
  const peut = usePeutUtiliser();
  const peutEvts  = peut('calendrier.evenements');
  const peutVues  = peut('calendrier.vues');
  const peutGarde = peut('calendrier.gardes');
  const peutVac   = peut('calendrier.vacances');
  const peutEdt   = peut('edt');
  const peutLieux = peut('lieux');

  // Type du calendrier actif : la garde est active pour la co-parentalité. Les
  // calendriers issus de MoPCal portent le type LEGACY « enfant » → reconnu aussi.
  // Sans la fonction « Gardes », le calendrier se comporte comme un calendrier
  // ordinaire : ni bandeau, ni couleurs, ni demandes, ni bascule au double-clic.
  const estCoparent = peutGarde
    && ['coparentalite', 'enfant'].includes(calendriers.find(c => c.id === calIdActif)?.type || '');

  const auj = new Date();
  const [an, setAn]   = useState(auj.getFullYear());
  const [mo, setMo]   = useState(auj.getMonth());
  const [sel, setSel] = useState(auj.getDate());
  // Formulaire d'événement : état + validation + actions → hooks/useFormulaireEvenement.
  const [vueRecs, setVueRecs]   = useState(false); // page « Récurrences »
  const [vueEdt, setVueEdt]     = useState(false); // écran « Emploi du temps » (à venir)
  const [vueVac, setVueVac]     = useState(false); // page « Vacances »
  const [vueGardes, setVueGardes] = useState(false); // page « Gardes »
  const [vueConfig, setVueConfig] = useState(false); // page « Configuration »
  const [vueLieux, setVueLieux] = useState(false);   // calque « Lieux » (emplacements)
  const [choixLieuForm, setChoixLieuForm] = useState(false); // choix d'emplacement (formulaire)
  const [vueDemandes, setVueDemandes] = useState(false); // page « Demandes »
  const [vueSwitcher, setVueSwitcher] = useState(false); // sélecteur de calendriers (double-clic onglet)
  const [vueMoisAnnee, setVueMoisAnnee] = useState(false); // sélecteur mois/année (clic titre, vue Mois)
  const [titreDate, setTitreDate] = useState(false);       // sélecteur de jour (clic titre, vues Semaine/Jour)
  const [vueEvts, setVueEvts]     = useState(false);       // modale « événements du jour » (double-clic en vue Mois)
  const [evtInfo, setEvtInfo]     = useState(null);        // fiche INFO d'un événement (lecture seule) : { ev, dk }
  const [vueFiltre, setVueFiltre] = useState(null);        // feuille « Filtres » : 'cat' | 'type' | null
  const [catMasquees, setCatMasquees] = useState(() => new Set());  // ids catégorie/type MASQUÉS (exclusion)
  const [gridH, setGridH]         = useState(0);           // hauteur mesurée de la grille du mois → ligne = gridH/6 (fixe, alignée)
  // NAVIGATION entre modales : fermer une modale ouverte DEPUIS une autre
  // rouvre la précédente (liste du jour → fiche → formulaire, et retour).
  const [retourEvts, setRetourEvts] = useState(false);   // la fiche vient de la liste du jour
  const [retourRecs, setRetourRecs] = useState(false);   // la fiche vient de la page Récurrences
  const [retourForm, setRetourForm] = useState(null);    // { type:'evts'|'recs' } | { type:'fiche', e } — origine du formulaire
  const fermerFiche = () => {
    setEvtInfo(null);
    if (retourEvts) { setVueEvts(true); setRetourEvts(false); }
    if (retourRecs) { setVueRecs(true); setRetourRecs(false); }
  };
  // Pseudo-occurrence bâtie depuis un DOC de série (page Récurrences) : ouvre
  // la même fiche / édition que n'importe quelle occurrence.
  const evDeSerie = (r) => ({
    ...r, id: `rec_${r.id}`, recId: r.id, isRecurrent: true,
    recDef: r.recurrence || {}, recExceptions: r.exceptions || [], deplacements: r.deplacements || {},
    _calId: calId,
  });
  const [gardeVisible, setGardeVisible] = useState(true);       // afficher / cacher les infos de GARDE (bascule des Filtres)
  // Barre de VACANCES : bascule des Filtres, mais coupée d'office si la
  // fonction n'est pas incluse (ni traits, ni légende, ni page).
  const [vacVisible, setVacVisible] = useState(true);
  const [vue, setVue] = useState('mois');          // 'mois' | 'semaine' | 'jour'
  // Icône vs titre : par DÉFAUT icône en vue Semaine (7 colonnes, peu de place),
  // titre en vue Jour (une seule colonne, la place ne manque pas) — deux états
  // séparés (demande de Cédric, 23/08) pour que la préférence de l'une ne
  // change pas silencieusement celle de l'autre.
  const [evtIconeSem, setEvtIconeSem] = useState(true);
  const [evtIconeJour, setEvtIconeJour] = useState(false);
  const evtIcone = vue === 'jour' ? evtIconeJour : evtIconeSem;
  const [cfg, setCfg] = useState(CONFIG_DEFAUT);   // affichage horaire (semaine/jour)
  const [zoomH, setZoomH] = useState(null);        // hauteur d'heure ajustée au pincement (null = valeur config)
  const hauteurHeure = zoomH ?? cfg.hauteurHeure;  // zoom effectif de la grille Semaine/Jour

  // ── Formulaire d'événement : état + validation + actions (hook dédié). ──
  const dkSel = sel ? dateKey(an, mo, sel) : null;
  const {
    form, setForm, picker, setPicker, ouvrirPicker, formErr, busy,
    catForm, setCatForm, errDates, errHeures, heureFinManquante, formModifie, formEnErreur,
    CE, scrollForm, posErr, montrerErreurs, estompe, toggleJour, finParDefaut,
    ouvrirAjout, ouvrirAjoutCreneau, ouvrirEdition, ouvrirEditionRecurrence,
    enregistrer, supprimer, retablirException,
    deplacerOcc, setDeplacerOcc, deplacerOccurrence, supprimerSerie, gererRecurrence,
  } = useFormulaireEvenement({
    calId, edit, cfg, dkSel,
    allerJour: (d) => { setAn(d.getFullYear()); setMo(d.getMonth()); setSel(d.getDate()); },
    apresEnregistrer: () => {
      const r = retourForm; setRetourForm(null);
      if (r?.type === 'evts' || (r?.type === 'fiche' && retourEvts)) setVueEvts(true);
      if (r?.type === 'recs' || (r?.type === 'fiche' && retourRecs)) setVueRecs(true);
      if (r?.type === 'fiche') { setRetourEvts(false); setRetourRecs(false); }
    },
    onMajFiche: setEvtInfo,
  });

  // Appui long sur l'onglet Calendrier → sous-fonctions AUTORISÉES seulement
  // (gating par forfait/activation ; défaut permissif tant que non configuré).
  // Une fonction hors forfait reste VISIBLE dans le menu, mais grisée (🔒) :
  // l'utilisateur voit ce qui existe sans pouvoir l'ouvrir.
  useMenuOngletActif(ongletCle, [
    { entete: t('calendrier.menuSources') },
    { label: t('historique.filtre_recurrence'), icone: '🔁', disabled: !peut('calendrier.recurrences'), onPress: () => setVueRecs(true) },
    { label: t('famille.t.edt'), icone: '🕐', disabled: !peutEdt, onPress: () => setVueEdt(true) },
    { separateur: true },
    { entete: t('calendrier.menuConfiguration') },
    { label: t('calendrier.configuration'), icone: '⚙️', disabled: !peut('calendrier.configuration'), onPress: () => setVueConfig(true) },
    // Lieux retiré du menu Calendrier → carnet de RÉFÉRENCE, désormais dans le hub
    // Famille (section « Carnets », avec Contacts). Le lien reste au niveau de
    // l'événement (sélecteur de lieu du formulaire). L'écran EcranLieux ci-dessous
    // n'est plus atteint d'ici (conservé sans risque).
    { label: t('calendrier.vacancesFeries'), icone: '🏖️', disabled: !peutVac, onPress: () => setVueVac(true) },
    // Gardes : uniquement si le calendrier est de type co-parentalité. Sinon on
    // affiche une entrée « grisée » qui explique comment l'activer (Configuration).
    (!peutGarde
      ? { label: t('historique.filtre_garde'), icone: '🗓️', disabled: true }
      : (estCoparent
        ? { label: t('historique.filtre_garde'), icone: '🗓️', onPress: () => setVueGardes(true) }
        : { label: t('calendrier.gardesCoparentalite'), icone: '🔒', onPress: () => confirmerChoix(t('calendrier.gardesDesactiveesTitre'), t('calendrier.gardesDesactiveesMsg'), [{ text: t('calendrier.ouvrirConfiguration'), onPress: () => setVueConfig(true) }, { text: t('commun.fermer'), style: 'cancel' }]) })),
  ].filter(Boolean), edit);

  // Double-clic sur l'onglet Calendrier → sélecteur de calendriers (comme MoPCal).
  useDoubleTapOngletActif(ongletCle, () => setVueSwitcher(true), edit);

  // Clic sur l'onglet Calendrier → ferme toutes les modales et DÉZOOME d'un
  // niveau : Jour → Semaine → Mois (reste en Mois ensuite).
  useResetOnglet(ongletCle, () => {
    setVueRecs(false); setVueVac(false); setVueGardes(false); setVueConfig(false); setVueEdt(false); setVueLieux(false);
    setVueDemandes(false); setVueSwitcher(false); setVueMoisAnnee(false); setTitreDate(false);
    setVueEvts(false); setVueFiltre(null); setForm(null); setPicker(null); setEvtInfo(null); setChoixLieuForm(false);
    setRetourEvts(false); setRetourRecs(false); setRetourForm(null);
    setVue(v => (v === 'jour' ? 'semaine' : 'mois'));
  });

  // Vue Année : scroll auto vers le mois en cours à l'ENTRÉE dans la vue.
  const scrollPage = useRef(null);
  // (scroll auto de la vue Année : géré DANS VueAnnee — démonté en quittant.)

  // Config d'affichage (plage horaire, zoom, vue par défaut).
  const vueInit = useRef(false);
  useEffect(() => {
    if (!calId) return;
    chargerAffichageConfig(calId).then(c => {
      setCfg(c); setZoomH(null);
      if (vueInit.current) return;
      vueInit.current = true;
      // On FILTRE avant d'appliquer : monter la vue Semaine pour la redescendre
      // aussitot donnait un chargement visible « pour rien ».
      const horaire = c.vueDefaut === 'semaine' || c.vueDefaut === 'jour';
      setVue(horaire && !peutVues ? 'mois' : c.vueDefaut);
    }).catch(() => {});
  }, [calId, peutVues]);

  // Filet : la vue par défaut enregistrée (ou un ancien état) peut pointer sur
  // une vue horaire devenue interdite → on retombe sur le mois.
  useEffect(() => {
    if (!peutVues && (vue === 'semaine' || vue === 'jour')) setVue('mois');
  }, [peutVues, vue]);


  // Interaction sur un jour (vue Mois), inversée à la demande de Cédric (23/08) :
  //   • simple clic → sélectionne ET OUVRE la liste des événements du jour
  //     (avant : l'appui long) — geste principal, pas de délai d'attente
  //     d'un éventuel 2e tap (l'ancien double-clic-zoom est donc abandonné) ;
  //   • appui long  → sélectionne ET ZOOME sur le jour (vue Jour), reprend le
  //     rôle de l'ancien double-clic. Sans la fonction « Vues Semaine / Jour »,
  //     ne fait que sélectionner — on n'ouvre pas une vue interdite pour la
  //     refermer aussitôt (même règle que les icônes de vue, grisées et inertes).
  function tapJour(d) {
    setAn(d.getFullYear()); setMo(d.getMonth()); setSel(d.getDate());
    setVueEvts(true);
  }

  function onLongPressJour(d) {
    setAn(d.getFullYear()); setMo(d.getMonth()); setSel(d.getDate());
    if ((vue === 'semaine' || vue === 'mois') && peutVues) setVue('jour');
  }

  const allerAujourdhui = () => { const a = new Date(); setAn(a.getFullYear()); setMo(a.getMonth()); setSel(a.getDate()); };
  const surAuj = vue === 'annee' ? an === auj.getFullYear() : (an === auj.getFullYear() && mo === auj.getMonth() && (vue === 'mois' || sel === auj.getDate()));

  // Fenêtre affichée avec tampon ±7 j (pour les semaines à cheval sur 2 mois).
  const fenetre = useMemo(() => {
    const d1 = vue === 'annee' ? new Date(an, 0, 1) : new Date(an, mo, 1 - 7);
    const d2 = vue === 'annee' ? new Date(an, 11, 31) : new Date(an, mo, nbJours(an, mo) + 7);
    const f = (d) => dateKey(d.getFullYear(), d.getMonth(), d.getDate());
    return { d1, d2, min: f(d1), max: f(d2) };
  }, [an, mo, vue]);

  // Événements superposés multi-calendriers + récurrences du calendrier actif.
  const { map: mapChargee, recs, erreur: erreurEvenements, erreurRecs } = useEvenementsSuperposes({ visiblesIds, calendriers, couleurDe, fenetre, calId });
  // Sans la fonction « Événements », la grille reste vide partout (mois, année,
  // semaine, jour) : une seule coupure ici plutôt qu'un test par vue.
  const map = useMemo(() => (peutEvts ? mapChargee : {}), [peutEvts, mapChargee]);

  // Décale la date sélectionnée de n jours (traverse les mois).
  const decalerJours = (n) => { const d = new Date(an, mo, sel + n); setAn(d.getFullYear()); setMo(d.getMonth()); setSel(d.getDate()); };
  // Les 7 dates de la semaine contenant `sel` (lundi → dimanche).
  const semaineDates = useMemo(() => {
    const base = new Date(an, mo, sel);
    const lundi = new Date(an, mo, sel - ((base.getDay() + 6) % 7));
    return Array.from({ length: 7 }, (_, i) => new Date(lundi.getFullYear(), lundi.getMonth(), lundi.getDate() + i));
  }, [an, mo, sel]);
  const dkDe = (d) => dateKey(d.getFullYear(), d.getMonth(), d.getDate());

  // Grille de 42 cases = 6 semaines × 7, remplie de VRAIES dates : fin du mois
  // précédent + mois courant + début du mois suivant (comme MoPCal). Toujours 6
  // lignes → hauteur constante ; les jours hors mois sont affichés plus pâles.
  const cases = useMemo(() => {
    const debut = premierJourCol(an, mo);                 // décalage du 1er (0 = lundi)
    const lundi = new Date(an, mo, 1 - debut);            // lundi de la 1re semaine
    return Array.from({ length: 42 }, (_, i) => new Date(lundi.getFullYear(), lundi.getMonth(), lundi.getDate() + i));
  }, [an, mo]);

  // TOUJOURS 6 lignes (42 cases) → chaque semaine occupe un SLOT fixe, donc la
  // semaine N est au même niveau d'un mois à l'autre. Une semaine 100 % mois
  // adjacent est rendue INVISIBLE (ligne vide) mais garde son slot (cf. rendu).
  const semainesDuMois = useMemo(() => {
    const out = [];
    for (let i = 0; i < cases.length; i += 7) out.push(cases.slice(i, i + 7));
    return out;
  }, [cases]);
  // Hauteur FIXE d'une ligne = hauteur mesurée de la grille / 6 → chaque semaine
  // à une position absolue identique d'un mois à l'autre (repli 58 avant mesure).
  const ligneH = gridH > 0 ? gridH / 6 : 58;
  const allerSemaineDe = (d) => { setAn(d.getFullYear()); setMo(d.getMonth()); setSel(d.getDate()); setVue('semaine'); };

  // ── Garde à l'affichage + vacances/fériés + demandes (hook dédié). ──
  const {
    garde, parents, gardeCol, demandes, erreurDemandes, nbDemandes, montrerGarde,
    vacPeriodes, vacCfg, feriesMap, estVacances, ferieNom, estFerie,
    // `basculerGarde` reste expose par le hook mais n'a PLUS d'appelant : le
    // double-clic sur un jour zoome desormais sur la vue Jour. A recabler sur
    // un autre geste si le changement de garde depuis le mois doit revenir.
    gardeCalculee, gardeAff, excColVisible, rechargerParents,
  } = useGardeAffichage({
    calId, edit, estCoparent, gardeVisible, vue, an, mo, sel, cases, semaineDates,
    rechargeVacances: `${vueVac}|${vueGardes}`,
  });
  // Double-clic sur le numéro de semaine → vue Semaine (comme MoPCal).
  const dTapSem = useRef({ wi: null, t: 0 });
  const tapSemaine = (dPrem, wi) => {
    if (!dPrem) return;
    const now = Date.now();
    if (dTapSem.current.wi === wi && (now - dTapSem.current.t) < 320) { dTapSem.current = { wi: null, t: 0 }; allerSemaineDe(dPrem); }
    else dTapSem.current = { wi, t: now };
  };
  // Navigation par swipe : ‹ (droite) = précédent, › (gauche) = suivant.
  const naviguer = (dir) => {
    if (vue === 'mois') { dir < 0 ? moisPrec() : moisSuiv(); }
    else decalerJours((vue === 'jour' ? 1 : 7) * dir);
  };
  const swipeNav = useMemo(() => Gesture.Pan()
    .activeOffsetX([-24, 24]).failOffsetY([-18, 18]).runOnJS(true)
    .onEnd((e) => { if (Math.abs(e.translationX) < 55 || Math.abs(e.translationY) > 70) return; naviguer(e.translationX > 0 ? -1 : 1); }),
    [vue, an, mo, sel]); // eslint-disable-line react-hooks/exhaustive-deps

  // Pincement (vues Semaine/Jour) → ajuste la hauteur d'une heure (zoom). La
  // police et l'icône des blocs suivent (cf. GrilleHoraire). Persiste à la fin.
  const hauteurRef = useRef(hauteurHeure); hauteurRef.current = hauteurHeure;
  const zoomBase = useRef(56);
  const pinchZoom = useMemo(() => Gesture.Pinch().runOnJS(true)
    .onBegin(() => { zoomBase.current = hauteurRef.current || 56; })
    .onUpdate((e) => { setZoomH(Math.max(28, Math.min(160, Math.round(zoomBase.current * e.scale)))); })
    .onEnd(() => { const h = hauteurRef.current; if (h != null && calId) sauvegarderAffichageConfig(calId, { ...cfg, hauteurHeure: h }).catch(() => {}); }),
    [calId, cfg]);
  const gestesGrille = useMemo(() => Gesture.Simultaneous(swipeNav, pinchZoom), [swipeNav, pinchZoom]);

  // Bascule un id (catégorie ou type) dans/hors du Set des MASQUÉS.
  const estAuj = (j) => j && an === auj.getFullYear() && mo === auj.getMonth() && j === auj.getDate();
  const nbEve  = (j) => (j ? (map[dateKey(an, mo, j)] || []).length : 0);
  // Jusqu'à 3 couleurs de calendrier distinctes présentes ce jour (pastilles).
  const couleursJour = (dk) => {
    const seen = [];
    for (const it of (map[dk] || [])) {
      if (estMasqueEvenement(it, catMasquees)) continue;   // filtre catégorie/type
      if (it.estPlage) continue;   // les plages ont leur BARRE continue (pas de point)
      const c = it._couleur || UI.accent; if (!seen.includes(c)) seen.push(c); if (seen.length >= 3) break;
    }
    return seen;
  };

  const moisPrec = () => { setSel(1); if (mo === 0) { setMo(11); setAn(a => a - 1); } else setMo(m => m - 1); };
  const moisSuiv = () => { setSel(1); if (mo === 11) { setMo(0); setAn(a => a + 1); } else setMo(m => m + 1); };

  const eventsJour = useMemo(() => {
    const l = (dkSel && map[dkSel]) ? map[dkSel].filter(ev => !estMasqueEvenement(ev, catMasquees)) : [];
    // Ordre ANTÉchronologique : du dernier au premier ; « jour entier » (sans
    // heure) en tête. Tri NUMÉRIQUE en minutes (pas alphabétique : « 2h02 »
    // doit passer avant « 21h35 ») — formats « HH:MM » et « HhMM » acceptés.
    const cle = (e) => { const mn = parseHeureMin(String(e.heure || '').replace('h', ':')); return mn == null ? 100000 : mn; };
    return l.sort((a, b) => cle(b) - cle(a));
  }, [map, dkSel, catMasquees]);

  // Map filtrée (catégories/types masqués) pour les vues Semaine/Jour (GrilleHoraire).
  const mapVisible = useMemo(() => {
    if (!catMasquees.size) return map;
    const out = {};
    for (const dk of Object.keys(map)) out[dk] = (map[dk] || []).filter(ev => !estMasqueEvenement(ev, catMasquees));
    return out;
  }, [map, catMasquees]);

  // Résumé COMPACT d'une règle de récurrence (colonne droite des listes) :
  // « Tous les 2 jours · → 30/09 » — date courte JJ/MM comme les plages.
  const regleCourte = (rd) => {
    if (!rd) return '';
    const k = Number(rd.intervalle) || (rd.type === 'bimensuel' ? 2 : 1);
    const base = (rd.type === 'hebdo' && k > 1) ? t('calendrier.repetNSemaines', { n: k })
      : (rd.type === 'mensuel' && k > 1) ? t('calendrier.repetNMois', { n: k })
      : (rd.type === 'quotidien' && k > 1) ? t('calendrier.repetNJours', { n: k })
      : repetLabel(rd.type || 'quotidien');
    return `${base}${rd.dateFin ? ` · → ${frJM(rd.dateFin)}` : ''}`;
  };
  // Durée lisible entre deux heures HH:MM (même format que la fiche).
  const dureeCourte = (hd, hf) => {
    const d0 = parseHeureMin(hd), d1 = parseHeureMin(hf);
    if (d0 == null || d1 == null || d1 <= d0) return '';
    const mn = d1 - d0;
    return mn >= 60 ? `${Math.floor(mn / 60)} h${mn % 60 ? ` ${String(mn % 60).padStart(2, '0')}` : ''}` : `${mn} min`;
  };

  // Chargement des calendriers de l'espace : spinner tant que ce n'est pas prêt
  // (évite le flash de l'onboarding « crée ton premier calendrier » pendant le
  // chargement / le changement d'espace).
  if (edit && !pret) {
    return (
      <View style={[s.page, { alignItems: 'center', justifyContent: 'center', gap: 14 }]}>
        <ActivityIndicator size="large" color={UI.accent} />
        <Text style={s.chargeTxt}>{t('calendrier.chargement')}</Text>
      </View>
    );
  }

  // Aucun calendrier à afficher, données prêtes :
  //   • une ÉCOUTE A ÉCHOUÉ → ce n'est PAS « aucun calendrier », c'est « on n'a
  //     pas pu vérifier ». Sans cette distinction, un refus de règle passager
  //     déclenchait l'onboarding « crée ton premier calendrier » chez un
  //     utilisateur qui en a — le pire des 5 écoutes muettes signalées le
  //     11/08 (COWORK.md) : il croit avoir tout perdu.
  //   • propriétaire d'un espace, lecture OK → onboarding « crée ton premier calendrier » ;
  //   • simple membre (aucun accès accordé), lecture OK → état vide neutre (il ne crée pas
  //     dans un espace qu'il ne possède pas ; un propriétaire doit lui donner accès).
  if (edit && pret && !aCalendrierEspace) {
    if (erreurLectureCal) {
      return (
        <View style={s.page}>
          <PageHeader titre={t('calendrier.titre')} iconeTitre="📅" />
          <View style={[s.bandeau, { margin: 14 }]}>
            <Text style={s.bandeauTxt}>⚠️ {t('calendrier.erreurLectureListe')}</Text>
            <Text style={s.bandeauDet}>{erreurLectureCal}</Text>
          </View>
        </View>
      );
    }
    if (peutCreer) return <PremierCalendrier onCree={(id) => setCalIdActif(id)} />;
    return (
      <View style={s.page}>
        <PageHeader titre={t('calendrier.titre')} iconeTitre="📅" />
        <EmptyState icon="📅" texte={t('calendrier.aucunAccessible')}
          aide={t('calendrier.aucunAccessibleAide')} />
      </View>
    );
  }

  return (
    <View style={s.page}>
      <PageHeader titre={t('calendrier.titre')} iconeTitre="📅">
        <View style={s.seg}>
          {[['annee', 'th', t('calendrier.vueAnCourt')], ['mois', 'calendar-alt', t('finances.mois')], ['semaine', 'calendar-week', t('calendrier.vueSemCourt')], ['jour', 'calendar-day', t('finances.triJour')]].map(([v, ic, lab]) => {
            // Vues horaires non incluses au forfait : icône GRISÉE et inerte —
            // on la garde visible pour que l'utilisateur sache qu'elle existe.
            const bloque = !peutVues && (v === 'semaine' || v === 'jour');
            return (
              // Re-taper la vue Sem./Jour déjà active bascule événements ICÔNE ↔ TITRE.
              <TouchableOpacity key={v} style={[s.segBtn, vue === v && s.segOn, bloque && s.segOff]}
                accessibilityLabel={lab} disabled={bloque}
                onPress={() => {
                  if (bloque) return;   // filet : `disabled` peut arriver apres le tap
                  if (vue === v && v === 'semaine') return setEvtIconeSem(x => !x);
                  if (vue === v && v === 'jour') return setEvtIconeJour(x => !x);
                  return setVue(v);
                }}>
                <FontAwesome5 name={ic} size={13} solid color={vue === v ? '#fff' : (bloque ? UI.faint : UI.muted)} />
              </TouchableOpacity>
            );
          })}
        </View>
        <AideFonction cle="calendrier" />
        {/* Ajout : seulement si la fonction « Événements » est ouverte. */}
        {edit && peutEvts && !!dkSel && (
          <TouchableOpacity onPress={ouvrirAjout} hitSlop={HIT} accessibilityLabel={t('calendrier.ajouterEvenement')}><Text style={s.hIc}>＋</Text></TouchableOpacity>
        )}
      </PageHeader>
      <View style={s.enTeteFixe}>
        {/* Écoute PARTIELLEMENT en échec (au moins un calendrier trouvé) :
            la liste peut être incomplète, on le dit sans bloquer l'écran. */}
        {!!erreurLectureCal && (
          <View style={[s.bandeau, { marginBottom: 8 }]}>
            <Text style={s.bandeauTxt}>⚠️ {t('calendrier.erreurLectureListe')}</Text>
            <Text style={s.bandeauDet}>{erreurLectureCal}</Text>
          </View>
        )}
        {/* Événements/récurrences/EDT d'un des calendriers superposés en
            échec : la grille se remplit quand même avec ce qui a pu être lu —
            ce n'est pas « rien ce mois-ci ». */}
        {!!erreurEvenements && (
          <View style={[s.bandeau, { marginBottom: 8 }]}>
            <Text style={s.bandeauTxt}>⚠️ {t('calendrier.erreurLectureEvenements')}</Text>
            <Text style={s.bandeauDet}>{erreurEvenements}</Text>
          </View>
        )}
        {/* Filtres CATÉGORIES et TYPES (deux chips, comme MoPCal) + CALENDRIER
            ACTIF à droite (tap → switcher, comme le double-clic d'onglet). */}
        <View style={s.filtreBar}>
          {(() => {
            const nbCat = CATEGORIES_EVT.filter(c => catMasquees.has(c.id)).length;
            const nbTyp = TYPE_FILTRES.filter(f => catMasquees.has(f.id)).length + (catMasquees.has('type:autre') ? 1 : 0);
            return (
              <>
                <TouchableOpacity style={[s.filtreChip, nbCat > 0 && s.filtreChipOn]}
                  onPress={() => setVueFiltre('cat')} accessibilityLabel={t('calendrier.categories')}>
                  <FontAwesome name="filter" size={13} color={nbCat > 0 ? UI.accent : UI.muted} />
                  <Text style={[s.filtreChipTxt, nbCat > 0 && s.filtreChipTxtOn]}> {t('calendrier.categories')}{nbCat > 0 ? `  ·  ${nbCat}` : ''}</Text>
                </TouchableOpacity>
                <TouchableOpacity style={[s.filtreChip, nbTyp > 0 && s.filtreChipOn]}
                  onPress={() => setVueFiltre('type')} accessibilityLabel={t('calendrier.types')}>
                  <Text style={[s.filtreChipTxt, nbTyp > 0 && s.filtreChipTxtOn]}>📌 {t('calendrier.types')}{nbTyp > 0 ? `  ·  ${nbTyp}` : ''}</Text>
                </TouchableOpacity>
              </>
            );
          })()}
          <View style={{ flex: 1 }} />
          {(() => {
            const calActif = calendriers.find(c => c.id === calIdActif);
            if (!calActif) return null;
            return (
              <TouchableOpacity style={s.calActifChip} onPress={() => setVueSwitcher(true)}
                accessibilityLabel={t('calendrier.definirActif')}>
                <View style={[s.calActifPastille, { backgroundColor: calActif.couleur || UI.accent }]}>
                  <Text style={s.calActifEmoji}>{calActif.emoji || '📅'}</Text>
                </View>
                <Text style={s.calActifNom} numberOfLines={1}>{calActif.nom || t('calendrier.titre')}</Text>
                <Text style={s.calActifChev}>▾</Text>
              </TouchableOpacity>
            );
          })()}
        </View>

        <View style={s.barreMois}>
          <TouchableOpacity onPress={vue === 'annee' ? () => setAn(a => a - 1) : vue === 'mois' ? moisPrec : () => decalerJours(vue === 'jour' ? -1 : -7)} hitSlop={HIT}><Text style={s.nav}>‹</Text></TouchableOpacity>
          <View style={s.titreZone}>
            <TouchableOpacity onPress={() => ((vue === 'mois' || vue === 'annee') ? setVueMoisAnnee(true) : setTitreDate(true))} activeOpacity={0.7}>
              <Text style={s.moisTxt}>{vue === 'annee'
                ? `${an}`
                : vue === 'mois'
                ? `${MOIS[mo]} ${an}`
                : vue === 'jour'
                  ? `${sel} ${MOIS[mo]} ${an}`
                  : `${semaineDates[0].getDate()} – ${semaineDates[6].getDate()} ${MOIS[semaineDates[6].getMonth()]}`}</Text>
            </TouchableOpacity>
            {/* « Aujourd'hui » = icône compacte, sur la même ligne que le titre.
                Toujours présente (grisée quand on y est déjà) → alignement constant. */}
            <TouchableOpacity onPress={allerAujourdhui} disabled={surAuj} hitSlop={HIT} accessibilityLabel={t('historique.aujourdhui')}>
              <Text style={[s.aujIc, surAuj && { opacity: 0.28 }]}>🎯</Text>
            </TouchableOpacity>
          </View>
          <TouchableOpacity onPress={vue === 'annee' ? () => setAn(a => a + 1) : vue === 'mois' ? moisSuiv : () => decalerJours(vue === 'jour' ? 1 : 7)} hitSlop={HIT}><Text style={s.nav}>›</Text></TouchableOpacity>
        </View>
      </View>

      {/* Le CONTENU de la vue défile ; les filtres et la barre restent fixes.
          Semaine/Jour : rendues HORS de ce ScrollView (plus bas) — un ScrollView
          imbriqué dans une page verrouillée bloquait le défilement de la grille. */}
      {(vue === 'mois' || vue === 'annee') && (
      <ScrollView ref={scrollPage} contentContainerStyle={s.corpsScroll}>
        {/* ── Vue ANNÉE : composant dédié (découpage). ── */}
        {vue === 'annee' && (
          <VueAnnee an={an} map={map} garde={garde} gardeCalculee={gardeCalculee}
            montrerGarde={montrerGarde} vacVisible={vacVisible && peutVac}
            estFerie={estFerie} estVacances={estVacances}
            catMasquees={catMasquees} gardeCol={gardeCol} colFerie={COL_FERIE} colVac={COL_VAC}
            scrollPage={scrollPage}
            onOuvrirMois={(m, jour) => { setMo(m); setSel(jour); setVue('mois'); }} />
        )}

        {vue === 'mois' && (
          <GrilleMois an={an} mo={mo} sel={sel} joursL={joursL} map={map} catMasquees={catMasquees}
            garde={garde} gardeAff={gardeAff} gardeCol={gardeCol} gardeCalculee={gardeCalculee}
            montrerGarde={montrerGarde} vacVisible={vacVisible && peutVac} estVacances={estVacances} estFerie={estFerie}
            parents={parents} semainesDuMois={semainesDuMois} ligneH={ligneH} gridH={gridH} setGridH={setGridH}
            vacPeriodes={vacPeriodes} feriesMap={feriesMap} swipeNav={swipeNav}
            numSemaine={numSemaine} dkDe={dkDe} couleursJour={couleursJour}
            onTapJour={tapJour} onLongJour={onLongPressJour} onTapSemaine={tapSemaine} />
        )}

        <View style={{ height: 24 }} />
      </ScrollView>
      )}

      {/* Grille horaire (vues Semaine / Jour) — HORS ScrollView de page : la
          grille défile en interne, le pincement règle l'échelle. */}
      {(vue === 'semaine' || vue === 'jour') && (
        <View style={{ flex: 1, paddingHorizontal: 14, paddingTop: 4 }}>
          <GestureDetector gesture={gestesGrille}>
          <View style={{ flex: 1, minHeight: 200 }}>
            <GrilleHoraire
              jours={vue === 'jour' ? [new Date(an, mo, sel)] : semaineDates}
              map={mapVisible} cfg={{ ...cfg, hauteurHeure }} garde={gardeAff} gardeCol={gardeCol} excCol={excColVisible} evtIcone={evtIcone} dkDe={dkDe}
              jourSel={new Date(an, mo, sel)}
              avecEntete
              onTapJour={(d) => tapJour(d)}
              onLongJour={(d) => {
                // Appui long sur l'EN-TÊTE d'un jour → liste des événements du
                // jour (même feuille que le double-clic d'un jour en vue Mois).
                setAn(d.getFullYear()); setMo(d.getMonth()); setSel(d.getDate());
                setVueEvts(true);
              }}
              onLongCreneau={edit && peutEvts ? ouvrirAjoutCreneau : undefined}
              onTapEvent={(ev, dk) => setEvtInfo({ ev, dk })}
            />
          </View>
          </GestureDetector>
          {/* Rappel « demandes de garde » du jour sélectionné, sous la grille. */}
          {!!sel && estCoparent && nbDemandes > 0 && (
            <View style={s.jour}>
              <TouchableOpacity style={s.demBadge} onPress={() => setVueDemandes(true)}>
                <Text style={s.demBadgeTxt}>{t('calendrier.demandesAAcquitter', { n: nbDemandes })}</Text>
              </TouchableOpacity>
            </View>
          )}
        </View>
      )}

      {/* Modale « événements du jour » — ouverte au DOUBLE-CLIC d'un jour (vue Mois).
          Ajout/édition ferment cette modale d'abord (jamais 2 feuilles empilées). */}
      <FeuilleEvenementsJour visible={vueEvts}
        titre={sel ? `${sel} ${MOIS[mo]} ${an}` : t('calendrier.journee')}
        edit={edit && peutEvts} ferieNomJour={dkSel ? ferieNom(dkSel) : null}
        eventsJour={eventsJour} regleCourte={regleCourte} dureeCourte={dureeCourte}
        urlItineraire={urlItineraireEv} ouvrirItineraire={ouvrirItineraireEv}
        onFermer={() => setVueEvts(false)}
        onAjouter={() => { setVueEvts(false); setRetourForm({ type: 'evts' }); ouvrirAjout(); }}
        onEditer={(ev) => {
          setVueEvts(false); setRetourForm({ type: 'evts' });
          if (ev.isRecurrent) ouvrirEditionRecurrence(ev); else ouvrirEdition(ev);
        }}
        onSupprimer={(ev) => { if (ev.isRecurrent && !ev.estDeplace) ajouterExceptionRecurrent(ev._calId || calId, ev.recId, dkSel).catch(() => {}); else supprimer(ev); }}
        onSupprimerSerie={(ev) => supprimerSerie(ev)}
        onDeplacer={(ev) => setDeplacerOcc({ ev, dk: dkSel, cal: ev._calId || calId, mode: (ev.isRecurrent && !ev.estDeplace) ? 'rec' : 'simple' })}
        onOuvrirFiche={(ev) => { setVueEvts(false); setRetourEvts(true); setEvtInfo({ ev, dk: dkSel }); }} />

      {/* Fiche INFO d'un événement (lecture seule) — clic sur un événement.
          ✏️ en en-tête = accès à l'écran de modification. */}
      <FicheEvenement evtInfo={evtInfo} edit={edit && peutEvts} calId={calId} calendriers={calendriers}
        MOIS={MOIS} joursL={joursL} repetLabel={repetLabel}
        onFermer={fermerFiche}
        onEditer={() => {
          const e = evtInfo; if (!e) return;
          setEvtInfo(null); setRetourForm({ type: 'fiche', e });
          if (e.ev.isRecurrent) ouvrirEditionRecurrence(e.ev); else ouvrirEdition(e.ev, e.dk);
        }}
        onDeplacer={() => {
          const e = evtInfo; if (!e) return; fermerFiche();
          setDeplacerOcc({ ev: e.ev, dk: e.dk, cal: e.ev._calId || calId, mode: (e.ev.isRecurrent && !e.ev.estDeplace) ? 'rec' : 'simple' });
        }}
        onSupprimer={() => {
          const e = evtInfo; if (!e) return; fermerFiche();
          if (e.ev.isRecurrent && !e.ev.estDeplace) gererRecurrence(e.ev, e.dk);
          else supprimer(e.ev);
        }}
        onRetablirException={retablirException} />

      <FormulaireEvenement
        form={form} setForm={setForm} formErr={formErr}
        errDates={errDates} errHeures={errHeures} heureFinManquante={heureFinManquante}
        formModifie={formModifie} formEnErreur={formEnErreur} busy={busy}
        enregistrer={enregistrer} montrerErreurs={montrerErreurs} estompe={estompe} CE={CE}
        posErr={posErr} scrollForm={scrollForm}
        picker={picker} setPicker={setPicker} ouvrirPicker={ouvrirPicker} finParDefaut={finParDefaut}
        repetCourt={repetCourt} toggleJour={toggleJour} joursL={joursL}
        calId={calId} setChoixLieuForm={setChoixLieuForm}
        onFermer={() => {
          setForm(null);
          const r = retourForm; setRetourForm(null);
          if (r?.type === 'fiche') setEvtInfo(r.e);
          else if (r?.type === 'evts') setVueEvts(true);
          else if (r?.type === 'recs') setVueRecs(true);
        }} />

      {/* Page « Récurrences » (appui long sur l'onglet Calendrier). */}
      <FeuilleRecurrences visible={vueRecs} recs={recs} edit={edit} joursL={joursL} repetLabel={repetLabel} erreurLecture={erreurRecs}
        onFermer={() => setVueRecs(false)}
        onEditer={(r) => { setVueRecs(false); setRetourForm({ type: 'recs' }); ouvrirEditionRecurrence(evDeSerie(r)); }}
        onSupprimerSerie={(r) => supprimerSerie(evDeSerie(r))}
        onOuvrirFiche={(r) => { setVueRecs(false); setRetourRecs(true); setEvtInfo({ ev: evDeSerie(r), dk: (r.recurrence || {}).dateDebut || dkSel }); }} />

      {/* EMPLOI DU TEMPS : éditeur en calque au-dessus du calendrier. */}
      {vueLieux && (
        <View style={[StyleSheet.absoluteFill, { zIndex: 60, backgroundColor: UI.bg }]}>
          <EcranLieux calId={calId} onFermer={() => setVueLieux(false)} />
        </View>
      )}
      {vueEdt && (
        <View style={[StyleSheet.absoluteFill, { zIndex: 60, elevation: 32 }]}>
          <EcranEDT calId={calId} onFermer={() => setVueEdt(false)} />
        </View>
      )}

      <FeuilleVacances visible={vueVac} calId={calId} onFermer={() => setVueVac(false)} />
      <FeuilleGardes visible={vueGardes} calId={calId} garde={garde} parents={parents}
        onFermer={() => setVueGardes(false)} />
      <FeuilleChoixLieu visible={choixLieuForm}
        lieux={calendriers.find(c => c.id === (form?.calEvt || calId))?.lieux || []}
        valeurId={form?.lieuId || ''}
        onSelect={(l) => setForm(x => ({ ...x, lieuId: l?.id || '', lieu: l?.nom || '' }))}
        onFermer={() => setChoixLieuForm(false)} />
      <FeuilleConfigCalendrier visible={vueConfig} calId={calId}
        onApplique={(norm) => { setCfg(norm); rechargerParents(); }}
        onFermer={() => setVueConfig(false)} />
      {vueDemandes && (
        <View style={[StyleSheet.absoluteFill, { zIndex: 60, backgroundColor: UI.bg }]}>
          <EcranDemandes calId={calId} demandes={demandes} erreurLecture={erreurDemandes} user={user} parents={parents} onFermer={() => setVueDemandes(false)} />
        </View>
      )}
      <SwitcherCalendriers visible={vueSwitcher} onClose={() => setVueSwitcher(false)} />

      <FeuilleFiltres visible={!!vueFiltre} ongletInitial={vueFiltre || 'cat'}
        estCoparent={estCoparent} avecVacances={peutVac} masques={catMasquees}
        gardeVisible={gardeVisible} vacVisible={vacVisible && peutVac}
        onAppliquer={({ masques, garde: g, vac }) => { setCatMasquees(masques); setGardeVisible(g); setVacVisible(vac); setVueFiltre(null); }}
        onFermer={() => setVueFiltre(null)} />
      <SelecteurMoisAnnee visible={vueMoisAnnee} an={an} mo={mo}
        onChoisir={(a, m, j) => { setAn(a); setMo(m); if (j) setSel(j); }}
        onFermer={() => setVueMoisAnnee(false)} />
      {/* Vues Semaine/Jour : le titre ouvre un sélecteur de JOUR.
          En vue Semaine, le jour choisi affiche la semaine qui le contient. */}
      {/* Déplacement d'une occurrence récurrente : choix du nouveau jour. */}
      <SelecteurDate visible={!!deplacerOcc} titre={t('calendrier.deplacerVersLe')}
        valeur={deplacerOcc?.dk || dateKey(an, mo, sel || 1)}
        onChange={deplacerOccurrence}
        onFermer={() => setDeplacerOcc(null)} />
      <SelecteurDate visible={titreDate}
        titre={vue === 'jour' ? t('calendrier.allerAuJour') : t('calendrier.allerALaSemaine')}
        valeur={dkSel || dateKey(an, mo, 1)}
        onChange={(dk) => { const [y, m, d] = dk.split('-').map(Number); setAn(y); setMo(m - 1); setSel(d); }}
        onFermer={() => setTitreDate(false)} />
    </View>
  );
}

const makeStyles = (UI) => StyleSheet.create({
  page:   { flex: 1, backgroundColor: UI.bg },
  enTeteFixe: { paddingHorizontal: 14, paddingTop: 14 },
  corpsScroll: { paddingHorizontal: 14, paddingBottom: 14, paddingTop: 4 },
  // 6 lignes qui se PARTAGENT la hauteur dispo à parts ÉGALES (flex) : espacement
  // des semaines maximal ET constant d'un mois à l'autre (toujours 6 slots, les
  // vides compris) → la légende descend au bas, contre la pub.
  hIc:    { fontSize: 20, color: UI.accent },
  filtreBar: { flexDirection: 'row', alignItems: 'center', gap: 12, marginBottom: 6 },
  filtreChip: { flexDirection: 'row', alignItems: 'center', paddingHorizontal: 12, paddingVertical: 6, borderRadius: 999, backgroundColor: UI.champ, borderWidth: 1, borderColor: UI.champBord },
  filtreChipOn: { backgroundColor: UI.accentTint, borderColor: UI.accent },
  filtreChipTxt: { fontSize: 12.5, fontWeight: '700', color: UI.ink },
  filtreChipTxtOn: { color: UI.accent },
  // Chip du CALENDRIER ACTIF (droite de la barre filtres) : pastille couleur du
  // calendrier + nom + ▾ ; tap = ouvre le switcher (comme le double-clic d'onglet).
  calActifChip: { flexDirection: 'row', alignItems: 'center', gap: 6, paddingLeft: 5, paddingRight: 9, paddingVertical: 4, borderRadius: 999, backgroundColor: UI.champ, borderWidth: 1, borderColor: UI.champBord, maxWidth: 180 },
  calActifPastille: { width: 22, height: 22, borderRadius: 11, alignItems: 'center', justifyContent: 'center', overflow: 'hidden' },
  calActifEmoji: { fontSize: 12 },
  calActifNom: { fontSize: 12.5, fontWeight: '700', color: UI.ink, flexShrink: 1 },
  calActifChev: { fontSize: 10, color: UI.faint, fontWeight: '800' },
  barreMois: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingHorizontal: 8, paddingVertical: 6 },
  nav:    { fontSize: 26, color: UI.accent, fontWeight: '300', width: 40, textAlign: 'center' },
  titreZone: { flex: 1, flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 8 },
  moisTxt:{ fontSize: 17, fontWeight: '800', color: UI.ink },
  aujIc:  { fontSize: 17 },
  chargeTxt: { fontSize: 13.5, color: UI.muted, fontWeight: '600' },
  bandeau:    { backgroundColor: UI.dangerTint || '#fdecec', borderRadius: 12, borderWidth: 0.5, borderColor: UI.danger || '#e5484d', padding: 11 },
  bandeauTxt: { fontSize: 13, fontWeight: '800', color: UI.danger || '#e5484d' },
  bandeauDet: { fontSize: 11.5, color: UI.muted, marginTop: 3 },
  semaine:{ flexDirection: 'row', marginTop: 6, marginBottom: 4 },
  // Barre vacances/férié CONTINUE entre jours adjacents (comme MoPCal) : plus
  // de marge horizontale ni d'arrondi — les segments se touchent.
  // Calque des barres de PLAGES multi-jours (continues sur la semaine), calé
  // après la colonne du numéro de semaine (26 px).
  // Aujourd'hui = ANNEAU autour du rond (pas de remplissage) → la couleur de
  // garde du jour reste visible.
  seg:    { flexDirection: 'row', backgroundColor: UI.champ, borderRadius: 9, padding: 2, marginRight: 6 },
  segBtn: { paddingHorizontal: 10, paddingVertical: 4, borderRadius: 7 },
  segOn:  { backgroundColor: UI.accent },
  segOff: { opacity: 0.4 },   // vue non incluse au forfait
  segTxtOn:{ color: '#fff' },
  gardeChipOn:{ backgroundColor: UI.accentTint, borderColor: UI.accent },
  gardeChipTxt:{ fontSize: 12.5, color: UI.ink, fontWeight: '600' },
  demBadge: { backgroundColor: '#FFF3E0', borderWidth: 1, borderColor: '#FFB74D', borderRadius: 10, paddingVertical: 8, paddingHorizontal: 12, marginBottom: 10 },
  demBadgeTxt: { fontSize: 12.5, color: '#C77700', fontWeight: '700' },
  jour:      { marginTop: 14 },
  // Fiche info événement (lecture seule)
  // Fiche d'événement (lecture) : en-tête pastille + badges, carte de détails.
  // Pleine largeur de cellule → la ligne est CONTINUE d'un jour à l'autre.
  jourChip:{ width: 38, height: 38, borderRadius: 999, alignItems: 'center', justifyContent: 'center', backgroundColor: UI.champ, borderWidth: 0.5, borderColor: UI.champBord },
  puceTxt:{ fontSize: 12.5, color: UI.ink, fontWeight: '600' },
  puceTxtOn:{ color: UI.accent, fontWeight: '800' },
  recSous:{ fontSize: 11.5, color: UI.muted, marginTop: 1 },
});
